// Intent capture & purchase tracking — demand data layer

const INTENT_KEY = "decision-os-intents";
const PURCHASE_KEY = "decision-os-purchases";

export interface IntentEvent {
  id: string;
  timestamp: string;
  type: "view_sellers" | "select_route" | "pre_checkout" | "redirect" | "buy_confirm" | "buy_cancel";
  productName: string;
  category: string;
  priceRange: string;
  selectedSeller?: string;
  selectedRoute?: string;
  query: string;
}

export interface PendingPurchase {
  id: string;
  productName: string;
  price: string;
  seller: string;
  deliveryTime: string;
  redirectedAt: string;
  confirmed?: boolean;
  rating?: number;
  feedback?: string;
}

function getIntents(): IntentEvent[] {
  try {
    const raw = localStorage.getItem(INTENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveIntents(events: IntentEvent[]): void {
  try { localStorage.setItem(INTENT_KEY, JSON.stringify(events.slice(-200))); } catch {}
}

export function captureIntent(event: Omit<IntentEvent, "id" | "timestamp">): void {
  const events = getIntents();
  events.push({
    ...event,
    id: `int-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    timestamp: new Date().toISOString(),
  });
  saveIntents(events);
}

function getPurchases(): PendingPurchase[] {
  try {
    const raw = localStorage.getItem(PURCHASE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function savePurchases(items: PendingPurchase[]): void {
  try { localStorage.setItem(PURCHASE_KEY, JSON.stringify(items.slice(-50))); } catch {}
}

export function addPendingPurchase(item: Omit<PendingPurchase, "id" | "redirectedAt">): PendingPurchase {
  const purchases = getPurchases();
  const entry: PendingPurchase = {
    ...item,
    id: `pur-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    redirectedAt: new Date().toISOString(),
  };
  purchases.unshift(entry);
  savePurchases(purchases);
  return entry;
}

export function confirmPurchase(id: string, feedback?: string): void {
  const purchases = getPurchases();
  const p = purchases.find((i) => i.id === id);
  if (p) { p.confirmed = true; p.feedback = feedback; savePurchases(purchases); }
}

export function cancelPurchase(id: string): void {
  const purchases = getPurchases();
  const p = purchases.find((i) => i.id === id);
  if (p) { p.confirmed = false; savePurchases(purchases); }
}

export function getPendingPurchases(): PendingPurchase[] {
  return getPurchases().filter((p) => p.confirmed === undefined);
}

export function getConfirmedPurchases(): PendingPurchase[] {
  return getPurchases().filter((p) => p.confirmed === true);
}

export function getAllPurchases(): PendingPurchase[] {
  return getPurchases();
}
